import { Chip } from '@/components/ui'
import type { PipelineStage, PipelineStageHistoryEntry } from '../api/types'
import { STAGE_LABELS, STAGE_VARIANT } from '../utils/pipeline-stage-meta'

/**
 * Vertical timeline of a freight_pipeline row's stage_history.
 *
 * Simpler than sales-inbox's StageHistoryTimeline — freight pipeline
 * entries are all real stage transitions (operator-driven or
 * reconciler/system-driven via `actor: "system"`), no anomaly or
 * extraction-marker shapes.
 */
export function StageHistoryTimeline({ entries }: { entries: PipelineStageHistoryEntry[] }) {
  if (!entries || entries.length === 0) {
    return (
      <p className="m-0 text-sm text-tertiary">No stage transitions recorded yet.</p>
    )
  }

  // Newest first — the JSONB array is appended in chronological order
  // server-side; reverse for display so the latest event reads first.
  const display = [...entries].reverse()

  return (
    <ol className="flex flex-col gap-2">
      {display.map((entry, i) => {
        const label = STAGE_LABELS[entry.stage as PipelineStage] ?? entry.stage
        const variant = STAGE_VARIANT[entry.stage as PipelineStage] ?? 'neutral'
        const isSystem = entry.actor === 'system'
        return (
          <li
            key={`${entry.at}-${entry.stage}-${i}`}
            className="flex items-start gap-3 border-l border-border-subtle pl-3"
          >
            <Chip size="xs" variant={variant} dot>
              {label}
            </Chip>
            <div className="min-w-0 flex-1">
              <div className="text-role-metadata">
                {formatTimestamp(entry.at)}
                {entry.actor ? ` · ${isSystem ? 'system (reconciler)' : entry.actor}` : null}
              </div>
              {entry.note ? (
                <div className="mt-0.5 text-xs text-secondary">{entry.note}</div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return iso
  }
}
